import SanitizedGrid from "../../../components/SanitizedGrid";
import React, { useEffect, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import { useFormState } from "react-final-form";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import CustomLabeled from "../../../components/layout/CustomLabeled";
import {
  getBusScheduleDays,
  getBusScheduleType,
} from "../../busSchedules/util";
import moment from "moment/moment";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import Collapse from "@material-ui/core/Collapse";
import { useDataProvider } from "react-admin";
import { uniq } from "lodash";
import {
  calculateDurationInString,
  getBusAssistantName,
  getBusAssistantsByIds,
  getDriverName,
  getDriversByIds,
  getSubcontractorName,
  getSubcontractorsByIds,
  getVehiclePlateNumber,
  getVehiclesByIds,
  processRoutesToRemoveSchoolLocation,
} from "../tools";

const useRowStyles = makeStyles({
  root: {
    "& > *": {
      borderBottom: "unset",
    },
  },
});

const Row = (props: any) => {
  const { row } = props;
  const [open, setOpen] = React.useState(false);
  const classes = useRowStyles();

  return (
    <React.Fragment>
      <TableRow className={classes.root}>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row">
          {row.address}
        </TableCell>
        <TableCell>{row.remark ?? "-"}</TableCell>
        <TableCell>
          {row.arrivalTime
            ? moment(row.arrivalTime, "HH:mm").format("HH:mm")
            : "-"}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse
            in={open}
            timeout="auto"
            unmountOnExit
            style={{
              width: "100%",
              padding: " 14px 4px",
              border: "1px solid #ccc",
              borderRadius: "8px",
              marginBottom: "14px",
            }}
          >
            <Box margin={1}>
              <Typography variant="subtitle1" gutterBottom component="div">
                List of Students
              </Typography>
              <Table size="small" aria-label="students">
                <TableHead>
                  <TableRow>
                    <TableCell>Code</TableCell>
                    <TableCell>Name</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {row.students.map((student: any, index: number) => {
                    return (
                      <TableRow key={index}>
                        <TableCell component="th" scope="row">
                          {student.locationCode}
                        </TableCell>
                        <TableCell component="th" scope="row">
                          {`${student.firstName} ${student.lastName}`}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

const FinalPreviewSection = () => {
  const { values } = useFormState();
  const provider = useDataProvider();

  const [finalRoutePlanData, setFinalRoutePlanData] = useState<any>(null);

  const [subcontractors, setSubcontractors] = useState<any[]>([]);
  const [drivers, setDrivers] = useState<any[]>([]);
  const [busAssistants, setBusAssistants] = useState<any[]>([]);
  const [vehicles, setVehicles] = useState<any[]>([]);

  useEffect(() => {
    let driverIds = values.routes
      .map((route: any) => route.driverId)
      .filter((id: number) => id != null);

    driverIds = uniq([
      ...driverIds,
      ...values.routes
        .map((route: any) => route.backupDriverIds ?? [])
        .flat()
        .filter((id: number) => id != null),
    ]);

    let busAssistantIds = values.routes
      .map((route: any) => route.busAssistantId)
      .filter((id: number) => id != null);

    busAssistantIds = uniq([
      ...busAssistantIds,
      ...values.routes
        .map((route: any) => route.backupBusAssistantIds ?? [])
        .flat()
        .filter((id: number) => id != null),
    ]);

    let vehicleIds = values.routes
      .map((route: any) => route.vehicleId)
      .filter((id: number) => id != null);

    vehicleIds = uniq([
      ...vehicleIds,
      ...values.routes
        .map((route: any) => route.backupVehicleIds ?? [])
        .flat()
        .filter((id: number) => id != null),
    ]);

    const subcontractorIds = values.routes
      .map((route: any) => route.assignedSubcontractor?.subcontractorId)
      .filter((id: number) => id != null);

    if (subcontractorIds.length > 0) {
      getSubcontractorsByIds(provider, subcontractorIds).then((result) => {
        setSubcontractors(result);
      });
    }

    if (driverIds.length > 0) {
      getDriversByIds(provider, driverIds).then((result) => {
        setDrivers(result);
      });
    }

    if (busAssistantIds.length > 0) {
      getBusAssistantsByIds(provider, busAssistantIds).then((result) => {
        setBusAssistants(result);
      });
    }

    if (vehicleIds.length > 0) {
      getVehiclesByIds(provider, vehicleIds).then((result) => {
        setVehicles(result);
      });
    }

    const finalRoutePlan = {
      ...values,
      routes: processRoutesToRemoveSchoolLocation(
        values.routes,
        values.busSchedule.type,
      ),
    };

    setFinalRoutePlanData(finalRoutePlan);
  }, [values]);

  return (
    <SanitizedGrid container spacing={2}>
      {!finalRoutePlanData && (
        <Grid item xs={12} style={{ textAlign: "center", marginTop: "20px" }}>
          <CircularProgress />
          <Typography variant="body2" style={{ marginTop: "10px" }}>
            Loading routes...
          </Typography>
        </Grid>
      )}

      {finalRoutePlanData ? (
        <>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Wave Info
            </Typography>
            <CustomLabeled name="Name">
              <Typography>{finalRoutePlanData.busSchedule.name}</Typography>
            </CustomLabeled>
            <CustomLabeled name="Route Type">
              <Typography>
                {getBusScheduleType(finalRoutePlanData.busSchedule.type)}
              </Typography>
            </CustomLabeled>
            <CustomLabeled name="Days of the Weeks">
              <Typography>
                {getBusScheduleDays(finalRoutePlanData.busSchedule)}
              </Typography>
            </CustomLabeled>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Next Effective Date
            </Typography>
            <CustomLabeled name="Date">
              <Typography>
                {finalRoutePlanData.nextEffectiveDate
                  ? moment(finalRoutePlanData.nextEffectiveDate).format(
                      "DD / MM / YYYY",
                    )
                  : "-"}
              </Typography>
            </CustomLabeled>
          </Grid>

          <Grid item xs={12}>
            <Divider style={{ margin: "14px 0px" }} />
          </Grid>

          <Grid
            item
            xs={12}
            style={{
              width: "100%",
              overflow: "auto",
              maxHeight: "700px",
            }}
          >
            <Typography variant="h6" gutterBottom>
              Routes Info
            </Typography>

            {Array.isArray(finalRoutePlanData.routes) &&
            finalRoutePlanData.routes.length > 0 ? (
              finalRoutePlanData.routes.map((route: any, index: number) => (
                <Card
                  key={index}
                  style={{
                    marginBottom: "24px",
                    border: "1px solid #E3E3E3",
                    borderRadius: "10px",
                    padding: "14px",
                  }}
                >
                  <CardContent>
                    <SanitizedGrid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1" gutterBottom>
                          {route.routeName}
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <CustomLabeled name="Start Time">
                          <Typography>
                            {route.startTime
                              ? moment(route.startTime, "HH:mm").format("HH:mm")
                              : "-"}
                          </Typography>
                        </CustomLabeled>
                        <CustomLabeled name="End Time">
                          <Typography>
                            {route.endTime
                              ? moment(route.endTime, "HH:mm").format("HH:mm")
                              : "-"}
                          </Typography>
                        </CustomLabeled>
                        <CustomLabeled name="Duration">
                          <Typography>
                            {calculateDurationInString(
                              route.startTime,
                              route.endTime,
                            )}
                          </Typography>
                        </CustomLabeled>
                      </Grid>

                      <Grid item xs={12}>
                        <CustomLabeled name="Bus Bay Number">
                          <Typography>{route.busBayNumber ?? "-"}</Typography>
                        </CustomLabeled>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <CustomLabeled name="Driver">
                          <Typography>
                            {route.driverId
                              ? getDriverName(drivers, route.driverId)
                              : "-"}
                          </Typography>
                        </CustomLabeled>
                        <CustomLabeled name="Backup Drivers">
                          {route.backupDriverIds.length > 0 ? (
                            route.backupDriverIds.map((driverId: number) => (
                              <Chip
                                size="small"
                                color="primary"
                                label={getDriverName(drivers, driverId)}
                              />
                            ))
                          ) : (
                            <Typography>-</Typography>
                          )}
                        </CustomLabeled>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <CustomLabeled name="Bus Assistant">
                          <Typography>
                            {route.busAssistantId
                              ? getBusAssistantName(
                                  busAssistants,
                                  route.busAssistantId,
                                )
                              : "-"}
                          </Typography>
                        </CustomLabeled>
                        <CustomLabeled name="Backup Bus Assistants">
                          {route.backupBusAssistantIds.length > 0 ? (
                            route.backupBusAssistantIds.map(
                              (busAssistantId: number) => (
                                <Chip
                                  size="small"
                                  color="primary"
                                  label={getBusAssistantName(
                                    busAssistants,
                                    busAssistantId,
                                  )}
                                />
                              ),
                            )
                          ) : (
                            <Typography>-</Typography>
                          )}
                        </CustomLabeled>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <CustomLabeled name="Vehicle">
                          <Typography>
                            {route.vehicleId
                              ? getVehiclePlateNumber(vehicles, route.vehicleId)
                              : "-"}
                          </Typography>
                        </CustomLabeled>
                        <CustomLabeled name="Backup Vehicles">
                          {route.backupVehicleIds.length > 0 ? (
                            route.backupVehicleIds.map((vehicleId: number) => (
                              <Chip
                                size="small"
                                color="primary"
                                label={getVehiclePlateNumber(
                                  vehicles,
                                  vehicleId,
                                )}
                              />
                            ))
                          ) : (
                            <Typography>-</Typography>
                          )}
                        </CustomLabeled>
                      </Grid>

                      <Grid item xs={12}>
                        <CustomLabeled name="Assigned Subcontractor">
                          <Typography>
                            {route.assignedSubcontractor?.subcontractorId
                              ? getSubcontractorName(
                                  subcontractors,
                                  route.assignedSubcontractor?.subcontractorId,
                                )
                              : "-"}
                          </Typography>
                        </CustomLabeled>
                        <CustomLabeled name="Vehicle Size">
                          <Typography>
                            {route.assignedSubcontractor?.vehicleSize ?? "-"}
                          </Typography>
                        </CustomLabeled>
                        <CustomLabeled name="Start Date">
                          <Typography>
                            {route.assignedSubcontractor?.startDate
                              ? moment(
                                  route.assignedSubcontractor?.startDate,
                                ).format("DD / MM / YYYY")
                              : "-"}
                          </Typography>
                        </CustomLabeled>
                        <CustomLabeled name="End Date">
                          <Typography>
                            {route.assignedSubcontractor?.endDate
                              ? moment(
                                  route.assignedSubcontractor?.endDate,
                                ).format("DD / MM / YYYY")
                              : "-"}
                          </Typography>
                        </CustomLabeled>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="subtitle1" gutterBottom>
                          Locations and Students Detail
                        </Typography>

                        <TableContainer component="div">
                          <Table aria-label="collapsible table">
                            <TableHead>
                              <TableRow>
                                <TableCell />
                                <TableCell>Address</TableCell>
                                <TableCell>Remark</TableCell>
                                <TableCell>Arrival Time</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {route.routeLocations.map(
                                (row: any, index: number) => (
                                  <Row key={index} row={row} />
                                ),
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Grid>
                    </SanitizedGrid>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Grid item xs={12}>
                <Card
                  style={{
                    marginBottom: "24px",
                    border: "1px solid #E3E3E3",
                    borderRadius: "10px",
                    padding: "14px",
                  }}
                >
                  <CardContent>
                    <Typography>No routes defined.</Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </>
      ) : (
        <></>
      )}
    </SanitizedGrid>
  );
};

export default FinalPreviewSection;
